import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { SuccessSendModal } from 'Components/DocumentForm';
import { Document } from 'Interfaces/Document';
import { UnauthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { parseJwtToken } from 'Utils/functions';
import { selectDocument, selectSignToken, selectUser } from 'Utils/selectors';

import { LoginModal, SignUpModal } from './components';

enum ModalType {
  AUTHORIZED = 'Authorized',
  LOGIN = 'Login',
  SIGNUP = 'SignUp',
}

interface DocumentSignModalProps {
  documentId?: Document['id'] | null;
  onClose: () => void;
}

const DocumentSignModal = ({ documentId, onClose }: DocumentSignModalProps) => {
  const navigate = useNavigate();
  const token: string | undefined = useSelector(selectSignToken);
  const document = useSelector(state => selectDocument(state, { documentId }));
  const { id } = useSelector(selectUser);
  const isAuthorizedUser = useMemo(
    () => (token ? parseJwtToken(token).sub === id : false),
    [id, token],
  );
  const [modalType, setModalType] = useState<ModalType>(
    isAuthorizedUser ? ModalType.AUTHORIZED : ModalType.SIGNUP,
  );

  const handleClose = useCallback(() => {
    onClose();

    navigate(UnauthorizedRoutePaths.BASE_PATH);
  }, [navigate, onClose]);

  const handleCloseSignUp = useCallback(() => {
    onClose();

    navigate(UnauthorizedRoutePaths.ABOUT, {
      state: { isSignUpClose: true },
    });
  }, [navigate, onClose]);

  switch (modalType) {
    case ModalType.AUTHORIZED:
      return <SuccessSendModal onClose={handleClose} document={document as Document} />;
    case ModalType.LOGIN:
      return (
        <LoginModal
          onClose={handleClose}
          onSignUpClick={() => setModalType(ModalType.SIGNUP)}
        />
      );
    case ModalType.SIGNUP:
      return (
        <SignUpModal
          document={document as Document}
          onClose={handleCloseSignUp}
          onSignInClick={() => setModalType(ModalType.LOGIN)}
        />
      );
    default:
      return null;
  }
};

export default DocumentSignModal;
